import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ToastOptions, useToast } from '../../../../lib/chakraUi/hooks';
import { AnyDocument, IncludeParams, Nullable } from '../../../../types';
import { createParams } from '../../../../utils/queries';
import { useRequestConstructor } from '../../../hooks/useRequestConstructor';
import { getApiErrorMessage } from '../../../logic';
import { unwrapNonPaginatedResponseDataAs } from '../../../logic/unwrapNonPaginatedResponseDataAs';
import { APPLICATION_BASE_PATH } from '../../../middle-office/application/constants/applicationBasePath';
import { ApiErrorResponse } from '../../../types';
import { NonPaginatedResponse } from '../../../types/NonPaginatedResponse';
import { getDocumentUserdataQueryKey } from '../../logic/getDocumentUserdataQueryKey';
import { updateDocumentUserdata } from '../../requests/updateDocumentUserdata';
import { DocumentParams } from '../../types/DocumentParams';

export const useDocumentUserdataUpdate = <
  DocumentType extends AnyDocument = AnyDocument,
  Payload = Record<string, unknown>,
>(
  documentParams: DocumentParams,
  documentId: DocumentType['id'],
  include?: string[],
  successMessage: Nullable<ToastOptions> = {
    title: 'Success',
    description: 'Update successful',
  },
) => {
  const { constructPost } = useRequestConstructor();
  const queryClient = useQueryClient();
  const toast = useToast();

  const mergedParams = useMemo(
    () => ({
      ...createParams<IncludeParams>({ include }),
    }),
    [include],
  );

  const queryKey = getDocumentUserdataQueryKey<DocumentType>(
    documentId,
    documentParams,
    mergedParams,
  );

  const { data, mutateAsync, ...rest } = useMutation<
    Awaited<ReturnType<typeof updateDocumentUserdata<DocumentType, Payload>>>,
    ApiErrorResponse,
    Payload
  >({
    mutationKey: queryKey,
    mutationFn: async (payload) =>
      updateDocumentUserdata<DocumentType, Payload>(
        constructPost,
        documentParams,
        documentId,
        mergedParams,
        payload,
      ),
    onError: (error) => {
      toast({
        title: 'Error!',
        description: getApiErrorMessage(error, 'Update failed'),
        type: 'error',
      });
    },
    onSuccess: (response) => {
      const document = response?.data;
      if (!document) return;
      queryClient.setQueryData<NonPaginatedResponse<DocumentType>>(
        queryKey,
        (existing) => {
          if (!existing) return existing;
          return {
            ...existing,
            data: {
              ...existing.data,
              ...document,
              userdata: { ...existing.data?.userdata, ...document.userdata },
            },
          };
        },
      );
      queryClient.setQueriesData<NonPaginatedResponse<DocumentType>>(
        { queryKey: [APPLICATION_BASE_PATH, documentId] },
        (existing) => {
          if (!existing) return existing;
          return {
            ...existing,
            data: {
              ...existing.data,
              userdata: { ...existing.data?.userdata, ...document.userdata },
            },
          };
        },
      );
      if (successMessage) {
        toast({
          ...successMessage,
          type: 'success',
        });
      }
    },
  });

  return {
    ...unwrapNonPaginatedResponseDataAs(data, 'document'),
    ...rest,
    updateDocumentUserdata: mutateAsync,
  };
};
